import classNames from 'classnames';
import { type UseMultipleSelectionStateChange, useCombobox, useMultipleSelection } from 'downshift';
import { constant, isEqual, noop } from 'lodash';
import { matchSorter } from 'match-sorter';
import maxSize from 'popper-max-size-modifier';
import {
  type AriaAttributes,
  type CSSProperties,
  type KeyboardEvent,
  type MouseEvent,
  memo,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { usePopper } from 'react-popper';
import { useDebouncedCallback } from 'use-debounce';

import { TriangleDown as TriangleDownIcon } from '@air/next-icons';
import { Portal } from '@air/primitive-portal';
import { cn, tailwindVariants, type VariantProps } from '@air/utils-tailwind';

import { InputClearButton } from '~/components/InputClearButton';
import { SelectChip } from '~/components/Select/components/SelectChip';
import { SelectChipOptionContainer } from '~/components/Select/components/SelectChipOptionContainer';
import { SelectCreateOption } from '~/components/Select/components/SelectCreateOption';
import { SelectEmptyOption } from '~/components/Select/components/SelectEmptyOption';
import { SelectLoadingOption } from '~/components/Select/components/SelectLoadingOption';
import { applyMaxSize, sameWidth } from '~/components/Select/popperModifiers';
import { SELECT_OPTION, SELECTED_CHIP } from '~/components/Select/shared/testIDs';
import { type DefaultChipType } from '~/components/Select/shared/types';
import { CLEAR_SELECTED_VALUES_BUTTON, SELECT_INPUT_FIELD } from '~/constants/testIDs';

const BASE_10_RADIX = 10;
const POPPER_OFFSET_Y = 8;
const UPDATE_POPPER_DEBOUNCE_MS = 200;

function alphabeticallySortedFilterFunc<T extends DefaultChipType>(
  items: T[],
  inputValue: string,
  selectedOptions: T[],
) {
  const matchedOptions = matchSorter(items, inputValue, { keys: ['label'] });
  const selectedValues = selectedOptions.map((option) => option.value);

  return matchedOptions.filter((option) => !selectedValues.includes(option.value));
}

// use to avoid alphabatising the initial list/results
export function orderedSortedFilterFunc<T extends DefaultChipType>(
  items: T[],
  inputValue: string,
  selectedOptions: T[],
) {
  const matchedOptions = matchSorter(items, inputValue, {
    baseSort: (a, b) => (a.index < b.index ? -1 : 1),
    keys: ['label'],
  });
  const selectedValues = selectedOptions.map((option) => option.value);

  return matchedOptions.filter((option) => !selectedValues.includes(option.value));
}

export type OnSelectionChangeArgs<T extends DefaultChipType = DefaultChipType> = {
  chips: T[];
  addedChips: T[];
  removedChips: T[];
};

export type ChipRenderer<T extends DefaultChipType> = (
  chip: T,
  onDeselect: (deselected: T) => void,
  meta?: { isOpen?: boolean },
) => ReactNode;

export type ListItemRenderer<T extends DefaultChipType> = (chip: T) => ReactNode;

export const select = tailwindVariants({
  base: "relative flex rounded-4 [&>[data-popper-escaped='true']]:hidden [&>[data-popper-reference-hidden='true']]:hidden",
  variants: {
    size: {
      medium: 'min-h-[32px]',
      large: 'min-h-[40px]',
      'extra-large': 'min-h-[48px]',
    },
  },
  defaultVariants: {
    size: 'large',
  },
});

export const inputSelect = tailwindVariants({
  base: 'm-0.5 rounded-4 border-none bg-transparent p-0 font-medium text-grey-11 outline-none ring-0 focus:border-none focus:ring-0',
  variants: {
    size: {
      medium: 'text-12',
      large: 'text-12',
      'extra-large': 'text-14',
    },
  },
  defaultVariants: {
    size: 'large',
  },
});

export const placeholderSelect = tailwindVariants({
  base: 'm-0.5 px-0.5 text-grey-7',
  variants: {
    size: {
      medium: 'text-16 md:text-12',
      large: 'text-16 md:text-12',
      'extra-large': 'text-16 md:text-14',
    },
  },
  defaultVariants: {
    size: 'large',
  },
});

export type SelectVariants = VariantProps<typeof select>;

export type SelectProps<T extends DefaultChipType = DefaultChipType> = {
  alwaysShowClearItemButton?: boolean;
  /**
   * opens on render
   */
  autoFocus?: boolean;
  /**
   * If `error` is truthy, it will stop the creation of a new option
   */
  beforeCreate?: (input: T) => { error?: string } | undefined;
  /**
   * Function to render a custom chip.
   */
  chipRenderer?: ChipRenderer<T>;
  className?: string;
  /**
   * Close dropdown menu when option is selected
   */
  closeMenuOnSelect?: boolean;
  /**
   * Verb used when typing new options. Defaults to `Create`
   */
  createVerb?: string;
  'data-testid'?: string;
  'data-field-name'?: string;
  /**
   * Whether or not the input should render as disabled.
   */
  disabled?: boolean;
  /**
   * Function used to both order the dropdown list,
   * and filter results from the searchable input
   */
  filterFunc?: (items: T[], inputValue: string, selectedOptions: T[]) => T[];
  /**
   * overlay takes the whole screen so click outside of dropdown hides it without any events passed down
   */
  hasDropdownOverlay?: boolean;
  /**
   * Will add a red box shadow around the input to indicate that there is an error within it.
   * Error messages should be handled outside of the component.
   */
  hasError?: boolean;
  /**
   * We don't always want to show the X
   */
  hideClearWhenNotFocused?: boolean;
  /**
   * True if the dropdown should highlight the first option on open
   */
  highlightFirstOptionOnOpen?: boolean;
  /**
   * True if the dropdown should highlight the first matching option on search
   */
  highlightFirstOptionOnSearch?: boolean;
  /**
   * Class names for the container surrounding the input.
   */
  inputContainerClassName?: string;
  inputContainerStyle?: CSSProperties;
  inputAriaDescribedBy?: AriaAttributes['aria-describedby'];
  inputAriaInvalid?: AriaAttributes['aria-invalid'];
  inputId?: string;
  /**
   * True if you want a clear button to show on the right side of the input
   */
  isClearable?: boolean;
  /**
   * True if you are loading a new set of options
   */
  isLoading?: boolean;
  /**
   * True if the component should render an input to search through its list of items.
   * IMPORTANT: If this is false, and onInputChange is defined, onInputChange will not be called
   * because theres no input to change.
   */
  isSearchable?: boolean;
  /**
   * True if only 1 option should be able to be selected at a time
   */
  isSingleSelect?: boolean;
  /**
   * Will keep the dropdown position stickied to the input.
   */
  isSticky?: boolean;
  /**
   * Function to render custom list items.
   */
  listItemRenderer?: ListItemRenderer<T>;
  /**
   * A value to set as the max height for the dropdown.
   */
  maxDropdownHeight?: number;
  /**
   * Text rendered when theres no options available.
   */
  noOptionsText?: string;
  /**
   * Click function for individual chips
   */
  onChipClick?: (chip: T) => void;
  /**
   * Function called when an item is created.
   */
  onCreate?: (label: string) => void;
  /**
   * Function that allows to filter out incorrect input while adding a new option, e.g. if we want to accept only correct emails
   */
  createOptionFilter?: (input: string) => boolean;
  /**
   * Function called when the input changes.
   */
  onInputChange?: (search: string) => void;
  /**
   * triggers whenever the options are opened. Useful for async loading options.
   */
  onOptionsOpen?: () => void;
  /**
   * Intended for use when the options list is closed. Be aware however that this effect will
   * fire frequently, including on mount.
   */
  onOptionsClose?: () => void;
  /**
   * Function called when an item is selected or deselected
   */
  onSelectionChange: (args: OnSelectionChangeArgs<T>) => void;
  /**
   * The options to render in the select list.
   */
  options: T[];
  /**
   * Placeholder for the button.
   */
  placeholder?: string;
  /**
   * Defaults to `true`
   */
  pressEnterToAdd?: boolean;
  /**
   * True to prevent the default behavior of pressing enter to add a chip when searching
   */
  preventPressEnterToAddWhenSearching?: boolean;
  /**
   * True if the combobox should have a border like an input
   */
  renderAsInput?: boolean;
  /**
   * The options to render next to the input.
   */
  selectedOptions: T[];
  /**
   * shows the right adorned triangle
   */
  showTriangle?: boolean;
  /**
   * Renders the dropdown in a portal attached to document.body,
   * escaping overflow/transform ancestors that would clip it.
   */
  usePortal?: boolean;
} & SelectVariants;

const _Select = <T extends DefaultChipType>({
  alwaysShowClearItemButton,
  autoFocus,
  beforeCreate = constant(undefined),
  chipRenderer,
  className,
  closeMenuOnSelect,
  createVerb,
  'data-testid': testId,
  'data-field-name': fieldName,
  disabled,
  filterFunc = alphabeticallySortedFilterFunc,
  hasDropdownOverlay,
  hasError,
  hideClearWhenNotFocused,
  highlightFirstOptionOnOpen = false,
  highlightFirstOptionOnSearch = false,
  inputAriaDescribedBy,
  inputAriaInvalid,
  inputContainerClassName,
  inputContainerStyle,
  inputId,
  isLoading,
  isSearchable,
  isSingleSelect,
  isSticky = false,
  isClearable,
  listItemRenderer,
  maxDropdownHeight,
  noOptionsText,
  onCreate,
  onInputChange,
  onOptionsOpen,
  onOptionsClose,
  onSelectionChange,
  options,
  placeholder = '',
  pressEnterToAdd = true,
  preventPressEnterToAddWhenSearching = false,
  renderAsInput,
  selectedOptions = [],
  showTriangle = false,
  size = 'large',
  usePortal = false,
  createOptionFilter = constant(true),
}: SelectProps<T>) => {
  const [isCreating, setIsCreating] = useState(false);
  const [selectedItems, setSelectedItemsState] = useState(selectedOptions);
  const [inputItems, setInputItems] = useState(filterFunc(options, '', selectedOptions));
  const comboBoxRef = useRef(null);
  const popoverRef = useRef(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const labelRef = useRef<HTMLLabelElement | null>(null);
  const inputValueRef = useRef<string>('');
  const selectedItemsRef = useRef<T[]>(selectedOptions);
  const optionsRef = useRef<T[]>(options);

  const onOptionsOpenRef = useRef(onOptionsOpen);
  onOptionsOpenRef.current = onOptionsOpen;

  const onOptionsCloseRef = useRef(onOptionsClose);
  onOptionsCloseRef.current = onOptionsClose;

  const { styles, attributes, forceUpdate } = usePopper(comboBoxRef.current, popoverRef.current, {
    placement: 'bottom-start',
    strategy: 'fixed',
    modifiers: [
      maxSize,
      applyMaxSize,
      sameWidth,
      {
        name: 'eventListeners',
        options: {
          scroll: isSticky,
          resize: false,
        },
      },
      {
        name: 'offset',
        options: {
          offset: [0, POPPER_OFFSET_Y],
        },
      },
    ],
  });

  const [debouncedUpdatePopper] = useDebouncedCallback(() => {
    if (forceUpdate) {
      forceUpdate();
    }
  }, UPDATE_POPPER_DEBOUNCE_MS);

  const [selectedOptionValues, selectedOptionLabels] = useMemo(() => {
    return [selectedItems.map((option) => option.value), selectedItems.map((option) => option.label)];
  }, [selectedItems]);

  const focusSearchInput = useCallback(
    (e: SyntheticEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (inputRef.current && isSearchable) {
        inputRef.current.focus();
      }
    },
    [isSearchable],
  );

  // This is called whenever downshift changes its internal selection state. We pass those changes
  // up to the parent component.
  const onSelectedItemsChange = useCallback(
    ({ selectedItems: chips }: UseMultipleSelectionStateChange<T>) => {
      if (!!inputRef.current && isSearchable && !closeMenuOnSelect) {
        inputRef.current.focus();
      }
      if (chips) {
        setSelectedItemsState(chips);

        const filteredItems = filterFunc(optionsRef.current, inputValueRef.current, chips);

        setInputItems(filteredItems);

        const previouslySelected = selectedItemsRef.current;
        selectedItemsRef.current = chips;

        let addedChips: T[] = [];
        let removedChips: T[] = [];

        if (isSingleSelect && chips.length === previouslySelected.length) {
          addedChips = chips;
          removedChips = previouslySelected;
        } else if (chips.length > previouslySelected.length) {
          const previousChipValues = previouslySelected.map((chip) => chip.value);
          addedChips = chips.filter((chip) => !previousChipValues.includes(chip.value));
        } else if (previouslySelected.length > chips.length) {
          const chipValues = chips.map((chip) => chip.value);
          removedChips = previouslySelected.filter((chip) => !chipValues.includes(chip.value));
        }
        onSelectionChange?.({ chips, addedChips, removedChips });
      }
    },
    [closeMenuOnSelect, filterFunc, isSearchable, isSingleSelect, onSelectionChange],
  );

  const { getDropdownProps, addSelectedItem, removeSelectedItem, setSelectedItems, reset } = useMultipleSelection({
    selectedItems,
    onSelectedItemsChange,
    itemToString: (item) => item.value,
  });

  const onClear = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      if (!isClearable) {
        return;
      }
      e.preventDefault();
      e.stopPropagation();
      reset();
    },
    [isClearable, reset],
  );

  const {
    isOpen,
    getMenuProps,
    getInputProps,
    getComboboxProps,
    getItemProps,
    openMenu,
    getLabelProps,
    selectItem,
    setInputValue,
    inputValue,
    highlightedIndex,
    closeMenu,
    setHighlightedIndex,
  } = useCombobox<T | null>({
    initialIsOpen: autoFocus,
    selectedItem: null,
    items: inputItems,
    onInputValueChange: ({ inputValue: _inputValue }) => {
      const normalizedInputVal = _inputValue || '';

      if (!!onInputChange && inputValueRef.current !== normalizedInputVal) {
        onInputChange(normalizedInputVal);
      }

      const filteredItems = filterFunc(options, normalizedInputVal, selectedItemsRef.current);

      if (labelRef.current) {
        labelRef.current.dataset.value = normalizedInputVal;
      }

      debouncedUpdatePopper();

      setInputItems(filteredItems);

      inputValueRef.current = normalizedInputVal;
    },
    stateReducer: (_, actionAndChanges) => {
      const { changes, type } = actionAndChanges;
      switch (type) {
        //TODO: 'changes' will include isOpen: false here, causing the menu to close if the input is blurred.
        // This is desired if you're clicking outside the element, but if you tab to the onClear button it will:
        // 1) close the menu
        // 2) reopen the menu
        // 3) return focus to the input.
        // Effectively preventing tabing to the clear button. However, if you return isOpen: true here the menu wont
        // close when you click outside the component. We need to find a way to allow tabbing within the input while
        // preserving the click outside functionality.
        case useCombobox.stateChangeTypes.InputBlur:
          return {
            ...changes,
            inputValue: '',
          };
        case useCombobox.stateChangeTypes.FunctionOpenMenu: {
          return {
            ...changes,
            isOpen: disabled ? false : changes.isOpen,
            ...(highlightFirstOptionOnOpen === true && {
              highlightedIndex: Array.isArray(inputItems) && inputItems.length > 0 ? 0 : -1,
            }),
          };
        }
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true,
            inputValue: '',
          };
        default:
          return changes;
      }
    },
    onStateChange: ({ type, selectedItem }) => {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          if (selectedItem) {
            if (isSingleSelect) {
              setSelectedItems([selectedItem]);
            } else if (selectedOptionValues.includes(selectedItem.value)) {
              removeSelectedItem(selectedItem);
            } else {
              addSelectedItem(selectedItem);
            }

            selectItem(null);
            if (closeMenuOnSelect) {
              closeMenu();
            }
          }
          break;
        default:
          break;
      }
    },
  });

  // Highlight the first option that matches the user's search.
  useEffect(() => {
    if (highlightFirstOptionOnSearch === false) {
      return;
    }

    setHighlightedIndex(inputItems.findIndex((item) => item.label.toLowerCase() === inputValue.toLowerCase()));
  }, [highlightFirstOptionOnSearch, inputItems, inputValue, setHighlightedIndex]);

  // Focus the input when the list is opened, and re-evalute the popper position.
  useEffect(() => {
    if (isOpen) {
      onOptionsOpenRef.current?.();
      if (forceUpdate) {
        forceUpdate();
      }
      if (!!inputRef.current && isSearchable) {
        inputRef.current.focus();
      }
    } else {
      onOptionsCloseRef.current?.();
    }
  }, [isOpen, forceUpdate, isSearchable]);

  // If the input value changes, and it would necessitate the create button showing, show the create button.
  // Else, hide it.
  useEffect(() => {
    if (!onCreate) {
      return;
    }

    if (inputValue.length > 0) {
      const hasChipAsOption = inputItems.some((item) => item.label.toLowerCase() === inputValue.toLowerCase());
      const hasChipSelected = selectedOptionLabels.includes(inputValue);
      const isInputValid = createOptionFilter(inputValue);

      if (isInputValid) {
        if (hasChipAsOption || hasChipSelected) {
          setIsCreating(false);
        } else {
          setIsCreating(true);
        }
      } else {
        setIsCreating(false);
      }
    } else {
      setIsCreating(false);
    }
  }, [createOptionFilter, inputItems, inputValue, onCreate, selectedOptionLabels]);

  // If new options are passed in, update the multi-selects internal state if theyre different.
  useEffect(() => {
    if (!isEqual(options, optionsRef.current)) {
      optionsRef.current = options;
      const filteredItems = filterFunc(options, inputValueRef.current, selectedItemsRef.current);
      setInputItems(filteredItems);
    }
  }, [filterFunc, options]);

  // If the selectedOptions prop changes, make sure its actually changed before updating
  // this internal state.
  useEffect(() => {
    if (!isEqual(selectedOptions, selectedItemsRef.current)) {
      selectedItemsRef.current = selectedOptions;
      setSelectedItemsState(selectedOptions);
      const filteredItems = filterFunc(optionsRef.current, inputValueRef.current, selectedOptions);
      setInputItems(filteredItems);
    }
  }, [filterFunc, selectedOptions]);

  // Adjust the popper positioning if the selected items changes
  // incase they've overflowed to a new row and the dropdown needs to reposition
  useEffect(() => {
    if (selectedItems && forceUpdate) {
      forceUpdate();
    }
  }, [selectedItems, forceUpdate]);

  // Add a new item when create is clicked
  const onClickCreate = useCallback(() => {
    if (!inputValue || !onCreate) {
      return;
    }
    const newChip = { label: inputValue, value: inputValue } as T;

    const { error } = beforeCreate(newChip) || { error: undefined };

    if (error) {
      return;
    }

    if (isSingleSelect) {
      setSelectedItems([newChip]);
      setSelectedItemsState([newChip]);
    } else {
      selectItem(newChip);
      setSelectedItemsState([...selectedItems, newChip]);
    }

    selectItem(null);

    selectedItemsRef.current = [...selectedItems, newChip];
    onCreate(inputValue);
    if (closeMenuOnSelect) {
      closeMenu();
      setTimeout(() => inputRef.current?.blur());
    }

    if (inputRef.current && isSearchable) {
      inputRef.current.focus();
    }
  }, [
    beforeCreate,
    closeMenu,
    closeMenuOnSelect,
    inputValue,
    isSearchable,
    isSingleSelect,
    onCreate,
    selectItem,
    selectedItems,
    setSelectedItems,
  ]);

  // Memoize popper styles so we're no repainting the div they're passed to every re-render.
  const popperStyles = useMemo<CSSProperties>(() => {
    const maxStylesHeight = styles.popper.maxHeight ? parseInt(styles.popper.maxHeight as string, BASE_10_RADIX) : null;

    return {
      ...styles.popper,
      borderRadius: 4,
      maxHeight: maxDropdownHeight && maxStylesHeight ? Math.min(maxStylesHeight, maxDropdownHeight) : 'unset',
      zIndex: 2,
      overflow: 'auto',
      boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2), 0px 1px 3px rgba(0, 0, 0, 0.15), 0px 0px 2px rgba(0, 0, 0, 0.25)',
    };
  }, [maxDropdownHeight, styles.popper]);

  const listItems = useMemo(() => {
    return inputItems.map((item, index) => {
      // We dont spread this because it attaches a "mousemove" prop that hinders performance,
      // and isnt used by the component.
      const itemProps = getItemProps({ item, index });
      const isActive = highlightedIndex === index;

      return (
        <button
          className={classNames('w-full items-start text-start', { isActive })}
          data-testid={`${SELECT_OPTION}-${item.label}`}
          key={`${item.label}${index}`}
          onClick={itemProps.onClick}
          onMouseEnter={itemProps.onMouseMove}
          onMouseLeave={itemProps.onMouseMove}
          id={itemProps.id}
          ref={itemProps.ref}
          role={itemProps.role}
          type="button"
        >
          {listItemRenderer ? (
            listItemRenderer(item)
          ) : (
            <SelectChipOptionContainer className={classNames({ 'bg-grey-4 [&>*]:bg-grey-5': isActive })}>
              <SelectChip chip={item} size={size} />
            </SelectChipOptionContainer>
          )}
        </button>
      );
    });
  }, [inputItems, getItemProps, highlightedIndex, listItemRenderer, size]);

  const inputProps = getInputProps(
    {
      'aria-describedby': inputAriaDescribedBy,
      'aria-invalid': inputAriaInvalid,
      id: inputId,
      ref: inputRef,
      size: 1,
      tabIndex: 0,
    },
    {
      /**
       * Suppress the ref error that occurs when the inputRef is not defined.
       * @see https://github.com/downshift-js/downshift/issues/1531
       */
      suppressRefError: true,
    },
  );

  // The chips to render next to the input.
  const selectedChips = useMemo(() => {
    return selectedItems.map((chip, index) => {
      if (chipRenderer) {
        return (
          <div {...inputProps} key={`selected-chip-${chip.value}-${index}`}>
            {chipRenderer(chip, removeSelectedItem, { isOpen })}
          </div>
        );
      }

      return (
        <SelectChip<T>
          data-testid={SELECTED_CHIP}
          chip={chip}
          size={size}
          key={`selected-chip-${chip.value}-${index}`}
          onDeselect={alwaysShowClearItemButton || isOpen ? removeSelectedItem : undefined}
        />
      );
    });
  }, [selectedItems, chipRenderer, inputProps, removeSelectedItem, isOpen, size, alwaysShowClearItemButton]);

  const showClearFieldButton = useMemo(() => {
    if (!isClearable) {
      return false;
    }
    if (hideClearWhenNotFocused && !isOpen) {
      return false;
    }

    return isClearable && !!selectedChips.length && !disabled;
  }, [disabled, hideClearWhenNotFocused, isClearable, isOpen, selectedChips]);

  const selectContent = useMemo(() => {
    return (
      <div
        className={cn(
          'relative flex w-full flex-wrap items-center justify-start rounded-4 bg-transparent transition-colors',
          inputContainerClassName,
          disabled ? 'pointer-events-none' : '',
          {
            'ring-2 ring-blue-9': isOpen,
            'ring-1 ring-red-9': !isOpen && hasError,
            'ring-1 ring-grey-5 hover:ring-grey-7': !isOpen && !hasError,
          },
          isOpen && hasError ? 'ring-2 ring-red-9' : '',
          isOpen && isSearchable ? 'cursor-text' : '',
          renderAsInput && !isOpen ? 'ring-1 ring-grey-5' : '',
          renderAsInput && !hasError && !isOpen ? 'hover:ring-1 hover:ring-grey-7' : '',
          !renderAsInput && !isOpen && 'hover:bg-grey-2',
          size === 'medium' ? 'px-1.5 py-0' : 'p-1.5',
        )}
        style={inputContainerStyle}
        {...getComboboxProps(
          getDropdownProps({
            onClick: isOpen ? focusSearchInput : openMenu,
            onFocus: isOpen ? noop : openMenu,
            ref: comboBoxRef,
            tabIndex: -1,
          }),
        )}
      >
        {selectedChips.length || isOpen ? (
          selectedChips
        ) : (
          <p className={placeholderSelect({ size, className: 'placeholder' })}>{placeholder}</p>
        )}
        {/* Auto-sizing input based on: https://css-tricks.com/auto-growing-inputs-textareas/#other-ideas */}
        {/** biome-ignore lint/a11y/noLabelWithoutControl: <This is handle by downshift> */}
        <label
          className={classNames('auto-width', {
            isHidden: isSingleSelect ? !isSearchable && !!selectedChips.length : !isSearchable,
            'cursor-text': isOpen && isSearchable,
          })}
          {...getLabelProps({ ref: labelRef })}
        >
          {isOpen && isSearchable && (
            <input
              className={inputSelect({ size, className: showClearFieldButton ? 'pr-6' : '' })}
              data-testid={SELECT_INPUT_FIELD}
              {...inputProps}
              onKeyDown={(e: KeyboardEvent) => {
                inputProps.onKeyDown?.(e);
                if (pressEnterToAdd && e.key === 'Enter') {
                  const isListItemSelected = highlightedIndex !== -1;

                  // Take no action: if the user typed into the search field and we're performing a search
                  const isSearching = !!isLoading && inputValue.length !== 0 && !isListItemSelected;

                  if (preventPressEnterToAddWhenSearching === true && isSearching) {
                    return;
                  }

                  // Take no action: if there's a list item already selected
                  if (isListItemSelected) {
                    return;
                  }

                  if (isCreating && onCreate) {
                    onClickCreate();
                    e.preventDefault();
                    e.stopPropagation();
                  }
                  if (!isCreating) {
                    const chip = inputItems.find((item) => item.label.toLowerCase() === inputValue.toLowerCase());

                    if (!chip) {
                      return;
                    }

                    setSelectedItems([...selectedItems, chip]);
                    setInputValue('');
                    e.preventDefault();
                    e.stopPropagation();
                  }
                }
              }}
            />
          )}
        </label>
      </div>
    );
  }, [
    inputContainerClassName,
    inputContainerStyle,
    disabled,
    isOpen,
    hasError,
    isSearchable,
    renderAsInput,
    size,
    getComboboxProps,
    getDropdownProps,
    focusSearchInput,
    openMenu,
    selectedChips,
    placeholder,
    isSingleSelect,
    getLabelProps,
    showClearFieldButton,
    inputProps,
    pressEnterToAdd,
    highlightedIndex,
    isLoading,
    inputValue,
    preventPressEnterToAddWhenSearching,
    isCreating,
    onCreate,
    onClickCreate,
    inputItems,
    setSelectedItems,
    selectedItems,
    setInputValue,
  ]);

  const menuProps: { id?: string } & Record<string, unknown> = getMenuProps({ ref: popoverRef });

  const dropdownMenu = (
    <div
      className={cn(usePortal && 'data-[popper-reference-hidden=true]:hidden')}
      style={popperStyles}
      {...attributes.popper}
      {...menuProps}
    >
      {isOpen && (
        <ul className="w-full list-none overflow-auto rounded-4 bg-grey-1 p-3">
          {isLoading ? <SelectLoadingOption /> : listItems}
          {(!isCreating || !inputValue) && !isLoading && !inputItems.length && (
            <SelectEmptyOption noOptionsText={noOptionsText} />
          )}

          {isCreating && !!inputValue && !!onCreate && (
            <SelectCreateOption
              onClick={onClickCreate}
              value={inputValue}
              hideSpacer={!inputItems.length && !isLoading}
              createVerb={createVerb}
            />
          )}
        </ul>
      )}
    </div>
  );

  return (
    <div data-testid={testId} data-field-name={fieldName} className={select({ size, className })}>
      {/* This must be rendered first, otherwise it covers selected chips and makes it impossible to remove them */}
      {isOpen && hasDropdownOverlay && <div className="fixed inset-0 bg-transparent" />}
      {/**
       * If it's open, we don't want a wrapping button so we don't have buttons nested in buttons
       */}
      {isOpen ? (
        <div className="flex flex-1">{selectContent}</div>
      ) : (
        <button
          aria-controls={menuProps.id}
          aria-describedby={inputAriaDescribedBy}
          aria-expanded={false}
          aria-haspopup="listbox"
          aria-invalid={inputAriaInvalid}
          className="flex flex-1 font-normal"
          data-testid={`${testId}_TRIGGER`}
          disabled={disabled}
          id={inputId}
          onClick={openMenu}
          type="button"
        >
          {selectContent}
        </button>
      )}
      {usePortal ? <Portal>{dropdownMenu}</Portal> : dropdownMenu}
      <div className="absolute inset-y-0 right-2.5 z-1 flex items-center">
        {showClearFieldButton && (
          <InputClearButton data-testid={CLEAR_SELECTED_VALUES_BUTTON} isVisible isFlex onClick={onClear} />
        )}
        {showTriangle && <TriangleDownIcon className="size-4 text-grey-10" />}
      </div>
    </div>
  );
};

export const Select = memo(_Select) as typeof _Select;
